import { BinaryToUintArray } from '../Utils/utils.js';
import { settings_borders_and_units, alarmMapping } from '../Constants/constants';
import { CreateSimulationInit, SIMULATION_API_VERSION } from '../Utils/simulation_utils.js';

import { createSimulationModel, initialSimulationZones } from '../Utils/simulation_presets';

import simulationOnMessage from './simulationOnMessage.js';

export function createSimulation(address, controller, user, wasm) {
  let simulation = {
    socket: null,
    reconnectTimer: null,
    stopped: false,
    format: 'json', // or 'msgpack'

    init() {
      clearTimeout(this.reconnectTimer);
      clearInterval(this.peng);
      this.reconnectTimer = null;
      this.controller = controller;
      this.controller.api_version = SIMULATION_API_VERSION;
      this.user = user;
      this.wasm = wasm;
      if (!this.zones) {
        this.zones = initialSimulationZones(controller.type);
        this.model = createSimulationModel(controller.type, this.zones);
        this.has_new_zone = false;
        this.hvac = { updated: false, data: null };
      }
      this.stopped = document.visibilityState === 'hidden';
      if (this.stopped) return false;
      this.socket = new WebSocket(address);
      const socket = this.socket;
      this.socket.binaryType = 'arrayBuffer';

      this.socket.onopen = () => {
        if (this.socket !== socket || this.stopped) return;
        const request = CreateSimulationInit(this.socket, controller.name, controller.type, user);
        request.internal_id = controller.id;
        this.sendWSMsg(request);
      };

      this.socket.onmessage = (event) => {
        if (this.socket !== socket || this.stopped) return;
        let data;

        if (this.format === 'msgpack') {
          try {
            const reader = new FileReader();
            reader.onload = () => {
              const binary_str = reader.result;
              const len = binary_str.length;
              const new_data = wasm.fromBinary(BinaryToUintArray(binary_str, len), len);

              data = JSON.parse(new_data);
            };
            reader.readAsBinaryString(event.data);
          } catch (e) {
            console.log('[global] Failed parsing json: ', event.data, e);
          }
        } else {
          data = JSON.parse(event.data);
        }

        simulationOnMessage(this, data);
      };

      this.socket.onclose = () => {
        if (this.socket !== socket || this.stopped || this.reconnectTimer) return;
        clearInterval(this.peng);
        this.reconnectTimer = setTimeout(() => this.init(), 1000);
      };

      // Interval sending keep-alive for simulation.
      this.peng = setInterval(() => {
        if (this.socket.readyState === 1) {
          this.sendWSMsg({ command: 'peng', internal_id: '' });
        }
      }, 5000);
    },

    sendWSMsg: function (req) {
      // Add cookie
      req.cookie = document.cookie;
      // Get binary string through webassembly
      let msg = JSON.stringify(req);

      if (req) {
        if (this.format === 'msgpack' && this.wasm.toBinary) {
          const binary_str = this.wasm.toBinary(msg);
          let binaryData = new Uint8Array(
            atob(binary_str)
              .split('')
              .map(function (c) {
                return c.charCodeAt(0);
              })
          );
          msg = binaryData;
        }

        this.socket.send(msg);
      }
    },

    send_alarm(event, zone_id, zone_name, alarm) {
      // add alarms
      let alarms = {};
      if (alarm.name === 'all') {
        for (const key in alarmMapping) {
          const borders_key = key.replace('state', 'setting');
          let value = 1;
          if (borders_key in settings_borders_and_units) {
            value =
              key.indexOf('low') !== -1
                ? settings_borders_and_units[borders_key][0]
                : settings_borders_and_units[borders_key][1];
          }

          alarms[key] = parseInt(value);
        }
      } else alarms[alarm.name] = parseInt(alarm.value);

      // Construct request.
      var json_request = {
        internal_id: this.controller.id,
        cookie: document.cookie,
        command: 'alarms_triggered',
        data: { alarms: alarms, timestamp: Date.now() }
      };
      if (zone_id !== undefined) {
        json_request['is_zoned'] = true;
        json_request['data']['has_zone_data'] = 1;
        json_request['data']['id'] = zone_id.toString();
        json_request['data']['name'] = zone_name;
      }

      console.log('[alarms] ', JSON.stringify(json_request));

      this.socket.send(JSON.stringify(json_request));
    },

    resolve_alarm(event, zone_id, zone_name, alarm) {
      // add alarm key(s)
      let alarm_keys = [];
      if (alarm.name === 'all') {
        for (const key in alarmMapping) alarm_keys.push(key);
      } else {
        alarm_keys.push(alarm.name);
      }
      // Construct request.
      var json_request = {
        internal_id: this.controller.id,
        cookie: document.cookie,
        command: 'alarms_resolved',
        data: { timestamp: Date.now(), alarm_keys: alarm_keys }
      };
      if (zone_id !== undefined) {
        json_request['is_zoned'] = true;
        json_request['data']['has_zone_data'] = 1;
        json_request['data']['id'] = zone_id.toString();
        json_request['data']['name'] = zone_name;
      }

      this.socket.send(JSON.stringify(json_request));
    },

    stop() {
      this.stopped = true;
      clearTimeout(this.reconnectTimer);
      clearInterval(this.peng);
      this.reconnectTimer = null;
      if (this.socket && (this.socket.readyState === 0 || this.socket.readyState === 1)) {
        this.socket.close();
      }
    },

    suspend() {
      this.stop();
    },

    resume() {
      if (!this.stopped) return false;
      return this.init() !== false;
    }
  };

  simulation.init();
  return simulation;
}
